import { ProductsCellRenderer } from '@/pages/OrderManagement/components/Grid/Columns/OrderColumnCellrenderers'
import type { components } from '@/types/api/v1'
import {
  DollarOutlined,
  EnvironmentOutlined,
  ExperimentOutlined,
  GoldOutlined,
  MinusCircleOutlined,
  SwapOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { OrderUpdatedTag } from '@components/OrderDispatch/OrderGrid/OrderUpdatedTag'
import { OrderFlagIcon } from '@components/_SiteWide/Flags/FlagIcon'
import { Horizontal, Texto, addCommasToNumber, useThemeContext } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import type { ValueGetterParams } from 'ag-grid-community'
import { Tooltip } from 'antd'
import classNames from 'classnames'
import React from 'react'

function IconColumn({ headerName, field, filterResolvedFlags = true, columnGroupShow = 'open' }) {
  return {
    headerName,
    field,
    headerTooltip: headerName,
    columnGroupShow,
    valueGetter: (params) => params?.data?.[field]?.name ?? '',
    filterValueGetter: (params) => params?.data?.[field]?.name,
    comparator: (a, b) => {
      if (a === b) return 0
      if (a === 'No Flag') return -1
      if (b === 'No Flag') return 1
      return a < b ? -1 : 1
    },
    cellRenderer: (params) => {
      if (params.node.group) return params?.value
      const flag = params?.data?.[field]
      if (!flag) return null
      return (
        <OrderFlagIcon
          row={{ unresolved_flags: [flag], id: params.data.order_id }}
          filterResolvedFlags={filterResolvedFlags}
          allowFlagResolve={false}
        />
      )
    },
  }
}

const costDiffStyle = (params) => {
  if (params?.value < 0)
    return {
      color: 'var(--theme-error)',
    }
  return {}
}

const milesDiffStyle = (params) => {
  if (params?.value > 0)
    return {
      color: 'var(--theme-error)',
    }
  return {}
}

export function useAuditReportColumnDefs(formatCurrency, setSelectedOrderId) {
  const { isDarkTheme } = useThemeContext()

  return React.useMemo(
    () => getColumnDefs(formatCurrency, setSelectedOrderId, isDarkTheme),
    [formatCurrency, setSelectedOrderId, isDarkTheme]
  )
}

function getColumnDefs(formatCurrency, setSelectedOrderId, isDarkTheme) {
  return [
    {
      headerName: 'Order',
      marryChildren: true,
      children: [
        {
          headerName: 'Number',
          field: 'order_number',
          headerTooltip: 'Number',
        },
        {
          headerName: 'Final Destinations',
          headerTooltip: 'Final Destinations',
          field: 'destinations',
          valueGetter: (params) =>
            (params?.data?.destinations ?? [])?.map((dest) => `${dest.location_name}, ${dest.city}`).join(', '),
          comparator: (_, __, aa, bb) => {
            let firstStore = parseInt(aa?.data?.destinations?.[0]?.location_name ?? 0)
            let secondStore = parseInt(bb?.data?.destinations?.[0]?.location_name ?? 0)

            if (isNaN(firstStore)) firstStore = aa?.data?.destinations?.[0]?.location_name
            if (isNaN(secondStore)) secondStore = bb?.data?.destinations?.[0]?.location_name

            if (firstStore === secondStore) return 0
            if (firstStore < secondStore) return -1
            return 1
          },
          cellRenderer: (params) => {
            if (params.node.group) return params?.value
            return (
              <Horizontal style={{ overflow: 'default', gap: '0.5rem' }}>
                {params?.data?.destinations?.map((dest, i) => (
                  <Texto key={i.toString()}>
                    <span style={{ fontWeight: 'bold' }}>{dest.location_name}</span>, {dest.city}
                  </Texto>
                ))}
              </Horizontal>
            )
          },
        },
        {
          headerName: 'Products',
          field: 'products',
          cellRenderer: ProductsCellRenderer,
          valueGetter: (params) => params?.data?.product_ratios?.map((product) => product.name),
          minWidth: 200,
          autoHeight: true,
        },
      ],
    },
    {
      headerName: 'Supply Cost',
      marryChildren: true,
      children: [
        {
          headerName: 'Supply Locked',
          field: 'supply_locked',
          valueGetter: ({ data, node }: ValueGetterParams<components['schemas']['OrderResponse'], boolean>) =>
            node?.group ? '' : data?.supply_locked ? 'Yes' : 'No',
        },
        {
          headerName: 'Used Optimal Supply',
          headerTooltip: 'Used Optimal Supply',
          field: 'used_optimal_supply',
          sort: 'asc',
          sortIndex: 1,
          valueGetter: (params) => (params?.node?.group ? '' : params?.data?.used_optimal_supply ? 'Yes' : 'No'),
        },
        {
          headerName: 'Optimal Cost',
          headerTooltip: 'Optimal Cost',
          type: 'rightAligned',
          field: 'optimal_cost',
          valueFormatter: (params) => formatCurrency(params?.value, true, 2),
          aggFunc: 'sum',
          columnGroupShow: 'open',
          filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'Final Cost',
          headerTooltip: 'Final Cost',
          type: 'rightAligned',
          field: 'final_cost',
          valueFormatter: (params) => formatCurrency(params?.value, true, 2),
          aggFunc: 'sum',
          columnGroupShow: 'open',
          filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'Initial Cost',
          headerTooltip: 'Initial Cost',
          field: 'initial_cost',
          type: 'rightAligned',
          valueFormatter: (params) => formatCurrency(params?.value, true, 2),
          aggFunc: 'sum',
          columnGroupShow: 'open',
          filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'Optimal vs Final Cost',
          headerTooltip: 'Optimal vs Final Cost',
          field: 'optimal_vs_final_cost',
          type: 'rightAligned',
          valueFormatter: (params) => formatCurrency(params?.value, true, 2),
          cellStyle: costDiffStyle,
          aggFunc: 'sum',
          filter: 'agNumberColumnFilter',
          sort: 'desc',
          sortIndex: 2,
        },
        {
          headerName: 'Initial vs Final Cost',
          headerTooltip: 'Initial vs Final Cost',
          field: 'initial_vs_final_cost',
          type: 'rightAligned',
          valueFormatter: (params) => formatCurrency(params?.value, true, 2),
          cellStyle: costDiffStyle,
          aggFunc: 'sum',
          columnGroupShow: 'open',
          filter: 'agNumberColumnFilter',
        },
      ],
    },

    {
      headerName: 'Loaded Miles',
      marryChildren: true,
      children: [
        {
          headerName: 'Optimal Loaded Miles',
          headerTooltip: 'Optimal Loaded Miles',
          type: 'rightAligned',
          field: 'optimal_loaded_miles',
          valueFormatter: (params) => addCommasToNumber(params.value),
          aggFunc: 'sum',
          columnGroupShow: 'open',
          filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'Final Loaded Miles',
          headerTooltip: 'Final Loaded Miles',
          field: 'final_loaded_miles',
          type: 'rightAligned',
          valueFormatter: (params) => addCommasToNumber(params.value),
          aggFunc: 'sum',
          filter: 'agNumberColumnFilter',
          columnGroupShow: 'open',
        },
        {
          headerName: 'Initial Loaded Miles',
          headerTooltip: 'Initial Loaded Miles',
          field: 'initial_loaded_miles',
          type: 'rightAligned',
          valueFormatter: (params) => addCommasToNumber(params.value),
          aggFunc: 'sum',
          columnGroupShow: 'open',
          filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'Optimal vs Final Loaded Miles',
          headerTooltip: 'Optimal vs Final Loaded Miles (Positive number indiciates further distance)',
          field: 'optimal_vs_final_loaded_miles',
          type: 'rightAligned',
          valueFormatter: (params) => addCommasToNumber(params.value),
          cellStyle: milesDiffStyle,
          aggFunc: 'sum',
          filter: 'agNumberColumnFilter',
          sort: 'desc',
          sortIndex: 3,
        },
        {
          headerName: 'Initial vs Final Loaded Miles',
          headerTooltip: 'Initial vs Final Loaded Miles (Positive number indiciates further distance)',
          field: 'initial_vs_final_loaded_miles',
          type: 'rightAligned',
          valueFormatter: (params) => addCommasToNumber(params.value),
          cellStyle: milesDiffStyle,
          aggFunc: 'sum',
          columnGroupShow: 'open',
          filter: 'agNumberColumnFilter',
        },
      ],
    },
    {
      headerName: 'Terminal / Supplier',
      marryChildren: true,
      children: [
        {
          headerName: 'Final Terminals',
          headerTooltip: 'Final Terminals',
          field: 'final_terminals',
          valueGetter: (params) => params?.data?.final_terminals?.join(', '),
        },
        {
          headerName: 'Initial Terminals',
          headerTooltip: 'Initial Terminals',
          field: 'initial_terminals',
          initialHide: true,
          columnGroupShow: 'open',
          valueGetter: (params) => params?.data?.initial_terminals?.join(', '),
        },
        {
          headerName: 'Optimal Terminals',
          headerTooltip: 'Optimal Terminals',
          field: 'optimal_terminals',
          initialHide: true,
          columnGroupShow: 'open',
        },
        {
          headerName: 'Final Suppliers',
          headerTooltip: 'Final Suppliers',
          field: 'final_suppliers',
          columnGroupShow: 'open',
          valueGetter: (params) => params?.data?.final_suppliers?.join(', '),
        },
        {
          headerName: 'Initial Suppliers',
          headerTooltip: 'Initial Suppliers',
          field: 'initial_suppliers',
          initialHide: true,
          columnGroupShow: 'open',
          valueGetter: (params) => params?.data?.initial_suppliers?.join(', '),
        },
        {
          headerName: 'Optimal Suppliers',
          headerTooltip: 'Optimal Suppliers',
          field: 'optimal_suppliers',
          initialHide: true,
          columnGroupShow: 'open',
        },
      ],
    },
    {
      headerName: 'Pricing Flags',
      marryChildren: true,
      children: [
        IconColumn({
          headerName: 'Final Flag',
          field: 'final_flag',
          filterResolvedFlags: true,
          columnGroupShow: null,
        }),
        IconColumn({ headerName: 'Initial Flag', field: 'initial_flag', filterResolvedFlags: false }),
        IconColumn({ headerName: 'Resolved Flag', field: 'resolved_flag', filterResolvedFlags: false }),

        {
          headerName: 'Resolved Reason',
          headerTooltip: 'Resolved Reason',
          field: 'resolved_flag_reason',
          columnGroupShow: 'open',
        },
        {
          headerName: 'Resolved',
          headerTooltip: 'Resolved',
          columnGroupShow: 'open',
          field: 'resolved_flag_date',
          filter: 'agDateColumnFilter',
          initialHide: true,
          valueGetter: (params) =>
            params?.data?.resolved_flag_date ? formatters.DATE_TIME(params.data.resolved_flag_date) : null,
        },
        {
          headerName: 'Resolved By',
          columnGroupShow: 'open',
          headerTooltip: 'Resolved By',
          initialHide: true,
          field: 'flag_resolved_by',
        },
      ],
    },
    {
      headerName: 'Order Creation',
      marryChildren: true,
      children: [
        {
          field: 'created_date',
          headerTooltip: 'Created Date',
          headerName: 'Created',
          initialHide: true,
          filter: 'agDateColumnFilter',
          valueGetter: (params) => (params?.data?.created_date ? formatters.DATE_TIME(params.data.created_date) : ''),
        },
        {
          field: 'created_by',
          headerTooltip: 'Created By',
          initialHide: true,
          headerName: 'Created By',
          valueGetter: (params) => params?.data?.created_by ?? 'Gravitate',
        },
      ],
    },
    {
      headerName: 'Order Updates',
      marryChildren: true,
      children: [
        {
          field: 'update_count',
          headerTooltip: 'Update Count',
          headerName: 'Update Count',
          type: 'rightAligned',
          aggFunc: 'sum',
          filter: 'agNumberColumnFilter',
          columnGroupShow: 'open',
        },
        {
          field: 'data_updates',
          headerName: 'Data Updates',
          headerTooltip: 'Data Updates: All types of structural changes made to the order across its lifecycle.',
          filter: 'agMultiColumnFilter',
          valueFormatter: (params) => params?.value,
          cellRenderer: (params) => {
            const updates = params?.data?.data_updates

            function getTruncatedUpdate(update: string) {
              switch (update) {
                case 'Volume':
                  return 'Vol'
                case 'Supply Only':
                  return 'Supply'
                case 'Smart Supply':
                  return 'Smart'
                case 'No Changes':
                  return 'None'
                case 'Trailer Swap':
                  return 'Swap'
                default:
                  return update.substring(0, 4)
              }
            }

            function getIcon(update: string) {
              switch (update) {
                case 'Destination':
                  return <EnvironmentOutlined />
                case 'Product':
                  return <ExperimentOutlined />
                case 'Volume':
                  return <GoldOutlined />
                case 'Supply Only':
                  return <DollarOutlined style={{ color: isDarkTheme ? 'black' : 'white' }} />
                case 'Smart Supply':
                  return <ThunderboltOutlined />
                case 'No Changes':
                  return <MinusCircleOutlined />
                case 'Trailer Swap':
                  return <SwapOutlined />
                default:
                  return null
              }
            }

            function getIconColor(update: string) {
              switch (update) {
                case 'Volume':
                  return 'var(--theme-optimal)'
                case 'Destination':
                  return 'var(--theme-success)'
                case 'Product':
                  return 'var(--theme-color-2)'
                case 'Smart Supply':
                  return 'var(--theme-warning)'
                case 'Supply Only':
                  return isDarkTheme ? 'white' : 'var(--theme-color-1)'
                case 'Trailer Swap':
                  return 'var(--theme-color-3)'
                case 'No Changes':
                  return 'var(--gray-500)'
                default:
                  return null
              }
            }

            if (params.node.group) return params?.value
            if (!updates) return null
            const multipleProducts = true
            return (
              <div style={{ display: 'flex' }}>
                {updates.map((update: string) => {
                  let iconColor = 'white'
                  if (update === 'Supply Only') {
                    iconColor = isDarkTheme ? '#ffffff' : 'white'
                  }

                  return (
                    <Tooltip key={update} placement='top' title={update}>
                      <div className='product-ratio-container mr-2 mb-1 flex'>
                        {multipleProducts && (
                          <div
                            style={{
                              backgroundColor: getIconColor(update) ?? 'var(--gray-800)',
                              width: 20,
                              height: 20,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              borderRadius: '50%',
                              color: iconColor,
                            }}
                          >
                            {getIcon(update)}
                          </div>
                        )}

                        <div
                          className={classNames(
                            'vertical-flex-center',
                            { 'px-3': !multipleProducts },
                            { 'pr-3 pl-2': multipleProducts }
                          )}
                          style={{ fontSize: '0.8em' }}
                        >
                          {getTruncatedUpdate(update)}
                        </div>
                      </div>
                    </Tooltip>
                  )
                })}
              </div>
            )
          },
          filterParams: {
            filters: [
              {
                filter: 'agTextColumnFilter',
              },
              {
                filter: 'agSetColumnFilter',
                filterParams: {
                  valueFormatter: (params) => params?.value,
                },
              },
            ],
          },
        },
        {
          field: 'reason_codes',
          headerName: 'All Reason Codes',
          headerTooltip: 'All Reason Codes: All reason codes that have been applied to the order across its lifecycle.',
          columnGroupShow: 'open',
          valueGetter: (params) => params?.data?.reason_codes?.join(', '),
        },
        {
          field: 'updated_by',
          headerName: 'Updated By',
          headerTooltip: 'Updated By',
          valueGetter: (params) => params?.data?.updated_by?.join(', '),
          initialHide: true,
          columnGroupShow: 'open',
        },
        {
          field: 'last_updated_by',
          headerName: 'Last Updated By',
          headerTooltip: 'Last Updated By',
          initialHide: true,
          columnGroupShow: 'open',
        },
        {
          field: 'updated_date',
          headerName: 'Updated',
          headerTooltip: 'Updated',
          minWidth: 200,
          columnGroupShow: 'open',
          filter: 'agDateColumnFilter',
          cellRenderer: (params) => {
            if (params.node.rowGroupColumn?.colId && params.node.rowGroupColumn.colId !== 'updated_date') return null

            return (
              <OrderUpdatedTag
                setAuditOrderId={setSelectedOrderId}
                order={{
                  id: params?.data?.order_id,
                  updated_by: params?.data?.last_updated_by,
                  updated_on: params?.data?.updated_date,
                  ...params?.data,
                }}
              />
            )
          },
        },
      ],
    },
    {
      headerName: 'Areas',
      marryChildren: true,
      children: [
        {
          field: 'market',
          headerTooltip: 'Market',
          headerName: 'Market',
        },
        {
          field: 'sector',
          headerTooltip: 'Sector',
          headerName: 'Sector',
          initialHide: true,
          columnGroupShow: 'open',
        },
      ],
    },
  ]
}
